import React from "react";
import { ADVERTISER, OVERVIEW, PIN } from "../../../../../core/theme/reportTheme";
import { formatDate, VISIBILITY_DESCRIPTION, VISIBILITY_CHANNELS, CHANNEL_NAMES } from "../../../../../core/data/marketOverviewText";
import {
  classifiedAs, rankText, comparisonLine, leaderLabel, averageOfText, soFar, coverageNote,
  YOUR_BUSINESS, TOP3_LABEL,
} from "../../../../../core/data/advertiserOverviewText";
import { useTypeMenu } from "../../../components/TypeMenu";

// The four cards across the top: pinned advertiser, visibility, searches seen
// in (you / #1 / top 3), and by device. Same type scale as Market Overview's
// stat cards: small caps title, one big number, small grey lines. Expanded, a
// card shows more of the same advertiserOverview() result.

function Card({ title, titleHint, expandButton, expanded, children }) {
  return (
    <div className={expanded ? ADVERTISER.cardExpanded : ADVERTISER.card}>
      <div className={ADVERTISER.cardHead}>
        <p className={ADVERTISER.cardTitle} title={titleHint}>{title}</p>
        {expandButton}
      </div>
      {children}
    </div>
  );
}

export function PinnedCard({ result, onSetType, onOpenLinkPanel, expandButton, expanded }) {
  const p = result.pinned;
  const { openMenu, menu } = useTypeMenu(onSetType);
  const listings = p.linkedListings;
  const first = listings[0];

  return (
    <Card title="Pinned advertiser" expandButton={expandButton} expanded={expanded}>
      <div className="flex items-center gap-1.5 min-w-0">
        <span className={ADVERTISER.name} title={p.displayName}>{p.displayName}</span>
        {p.isYourBusiness && <span className={PIN.tag}>{YOUR_BUSINESS}</span>}
        {p.matchedBy === "name" && <span className={OVERVIEW.tag} title="This listing has no website, so it is matched by its name.">No website</span>}
      </div>
      <p className={ADVERTISER.sub}>
        {classifiedAs(p.businessType)} · <button type="button" className={OVERVIEW.link} onClick={e => openMenu(p, e)}>change</button>
      </p>
      <div className={`flex items-center gap-1.5 min-w-0 ${ADVERTISER.sub}`}>
        {first && (
          <>
            <span className="truncate" title={listings.map(l => l.name).join(" · ")}>{first.name}</span>
            <ListingTag basis={first.basis} />
            {listings.length > 1 && <span className="shrink-0">+{listings.length - 1}</span>}
            <span className="shrink-0">·</span>
          </>
        )}
        <button type="button" className={`${OVERVIEW.link} shrink-0`} onClick={onOpenLinkPanel}>{first ? "Link a listing" : `Link a ${CHANNEL_NAMES.local} listing`}</button>
      </div>
      {expanded && (
        <div className="mt-3 text-sm text-slate-700 space-y-2">
          <p>{p.seen ? `First seen ${formatDate(p.firstSeenAt)} · last seen ${formatDate(p.lastSeenAt)}` : "Not seen in these searches."}</p>
          <p>{p.domain ? `Website: ${p.domain}` : "No website: matched by its listing name."}</p>
          <div>
            <p className="font-semibold text-slate-900">{CHANNEL_NAMES.local} listings counted under this advertiser</p>
            {listings.length === 0 ? <p className="text-slate-500">None.</p> : (
              <ul className="mt-1 space-y-0.5">
                {listings.map(l => <li key={l.name} className="flex items-center gap-1.5">{l.name} <ListingTag basis={l.basis} /></li>)}
              </ul>
            )}
          </div>
          {p.excludedByTypeFilter && <p className="text-amber-700">Its own business type is filtered out; your pinned advertiser is always shown.</p>}
          {p.excludedByAdvertiserFilter && <p className="text-amber-700">It isn't ticked in Advertisers; your pinned advertiser is always shown, compared with the ones ticked.</p>}
        </div>
      )}
      {menu}
    </Card>
  );
}

function ListingTag({ basis }) {
  return basis === "likely"
    ? <span className={OVERVIEW.tag} title="Matched by name - worth a check">likely match</span>
    : <span className={OVERVIEW.tag} title="Linked by hand">linked</span>;
}

export function VisibilityCard({ result, expandButton, expanded }) {
  const { rank } = result;
  const estimated = coverageNote(result);
  const note = `${soFar(result)}${estimated ? ` (${estimated.label})` : ""}`;
  return (
    <Card title="Visibility" titleHint={`Visibility: ${VISIBILITY_DESCRIPTION}`} expandButton={expandButton} expanded={expanded}>
      <div className={ADVERTISER.valueRow} title={estimated ? estimated.title : VISIBILITY_DESCRIPTION}>
        <span className={ADVERTISER.big}>{rank.visibility.score}%</span>
        <span className={ADVERTISER.sub}>{rank.visibility.searches} of {rank.visibility.of} searches{note}</span>
      </div>
      <p className={ADVERTISER.strong}>{rank.position != null ? rankText(result) : `Not ranked · ${rank.of} advertisers seen`}</p>
      {expanded && (
        <div className="mt-3 text-sm text-slate-700 space-y-1">
          <p>{comparisonLine(result)}</p>
          <p className="text-slate-500">Visibility = {VISIBILITY_DESCRIPTION}. Shopping doesn't count towards it.</p>
        </div>
      )}
    </Card>
  );
}

export function SearchesSeenCard({ result, expandButton, expanded }) {
  const { rank, comparison } = result;
  const of = rank.visibility.of;
  const leader = comparison.leader;
  const rows = [
    { label: result.pinned.displayName, searches: rank.visibility.searches, you: true },
    leader && { label: `${leaderLabel(leader)} ${leader.displayName}`, searches: leader.visibility.searches },
    comparison.top3.keys.length > 0 && { label: TOP3_LABEL, title: comparison.top3.keys.join(", "), searches: comparison.top3.visibility.searches },
  ].filter(Boolean);

  return (
    <Card title="Searches seen in · any channel" titleHint={VISIBILITY_DESCRIPTION} expandButton={expandButton} expanded={expanded}>
      <div className={ADVERTISER.bars}>
        {rows.map(r => (
          <div key={r.label} title={r.title ? `${r.label}: ${r.title}` : undefined}>
            <div className={ADVERTISER.barLine}>
              <span className={r.you ? `${ADVERTISER.barLabel} font-semibold text-slate-900` : ADVERTISER.barLabel} title={r.label}>{r.label}</span>
              <span className={ADVERTISER.barValue}>{averageOfText(r.searches, of)}</span>
            </div>
            <div className={ADVERTISER.barTrack}>
              <div className={r.you ? ADVERTISER.barYou : ADVERTISER.barOther} style={{ width: `${of > 0 ? (100 * (r.searches || 0)) / of : 0}%` }} />
            </div>
          </div>
        ))}
      </div>
      {expanded && comparison.top3.keys.length > 0 && (
        <p className="mt-3 text-sm text-slate-500">Top 3 others: {comparison.top3.keys.join(", ")}</p>
      )}
    </Card>
  );
}

export function DeviceCard({ result, expandButton, expanded }) {
  const { devices, comparison, sampling } = result;
  const leader = comparison.leader;
  const showTop3 = expanded && devices.mobile.top3;
  if (sampling.devices.length === 0) {
    return (
      <Card title="By device" expandButton={expandButton} expanded={expanded}>
        <p className={ADVERTISER.sub}>This snapshot didn't record devices.</p>
      </Card>
    );
  }
  return (
    <Card title="By device" titleHint={`Searches seen in ${VISIBILITY_CHANNELS}, per device`} expandButton={expandButton} expanded={expanded}>
      <table className={ADVERTISER.devTable}>
        <thead>
          <tr>
            <th className={ADVERTISER.devHead} />
            <th className={ADVERTISER.devHead}>You</th>
            {leader && <th className={ADVERTISER.devHead} title={leader.displayName}>{leaderLabel(leader)} {leader.displayName}</th>}
            {showTop3 && <th className={ADVERTISER.devHead}>{TOP3_LABEL}</th>}
          </tr>
        </thead>
        <tbody>
          {["mobile", "desktop"].map(d => (
            <tr key={d}>
              <td className={ADVERTISER.devLabel}>{d === "mobile" ? "Mobile" : "Desktop"}</td>
              <td className={`${ADVERTISER.devCell} font-semibold`}>{devices[d].you.searches} of {devices[d].you.of}</td>
              {leader && <td className={ADVERTISER.devCell}>{devices[d].leader.searches} of {devices[d].leader.of}</td>}
              {showTop3 && <td className={ADVERTISER.devCell}>{averageOfText(devices[d].top3.searches, devices[d].top3.of)}</td>}
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}
